import React, { useEffect, useState } from "react";
import { clearSession } from "../../shared/_helpers/session";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { getProfile, blockUnblockUser, incrementTotalMeetings } from "../../shared/_services/common.services";
import { formatMeetingsAvailable } from "../../shared/_helpers/meetingEntitlement";
import dateIcon from "../../assets/images/date_icon.svg";
import detailsIcon from "../../assets/images/details_icon.svg";
import SubscriptionIcon from "../../assets/images/subscription_icon.svg";
import Modal from "react-bootstrap/Modal";
import { useSnackbar } from "notistack";
import moment from "moment";
import { useAuth } from "../../context/AuthContext.jsx";
import { getSelectedPlanName, getSelectedPlanStatusLabel } from "../../shared/_helpers/selectedPlan";

function isPrivilegedRole(role) {
  return role === 'super-admin' || role === 'admin';
}

function isDocumentsNavActive(pathname) {
  return pathname.includes('documents') || pathname.includes('dashboard');
}

function isMeetingsNavActive(pathname) {
  return pathname.includes('meetings') || pathname.includes('dashboard');
}

function formatMemberSince(createdAt) {
  return createdAt ? moment(createdAt).format('dddd D MMMM YYYY') : '-';
}

function buildBlockConfirmMessage(clientDetail) {
  const action = clientDetail?.status ? 'block' : 'unblock';
  const firstName = clientDetail?.firstName ?? '';
  return `You are attempting to ${action} ${firstName}.`;
}

function resolveBlockButtonLabel(blockAction) {
  return blockAction === 'block' ? 'Block' : 'Unblock';
}

async function loadClientProfile(userId, setClientDetail, enqueueSnackbar, navigate) {
  const data = await getProfile(userId);
  if (data.status === 200) {
    setClientDetail(data?.data?.data?.user);
    return;
  }
  if (data?.response?.status !== 401) {
    return;
  }
  clearSession();
  enqueueSnackbar("Your session has been expired!", {
    variant: 'error',
    anchorOrigin: { horizontal: 'right', vertical: 'top' },
  });
  navigate('/login');
}

function navigateBackFromClientArea(navigate, currentPath, clientId) {
  if (currentPath.includes('/documents') || currentPath.includes('/meetings')) {
    navigate(`/dashboard/${clientId}?client=true`);
    return;
  }
  if (currentPath.includes('/dashboard')) {
    navigate('/users');
  }
}

function ClientAvatar({ clientDetail }) {
  if (!clientDetail.hasOwnProperty('firstName')) {
    return null;
  }
  return (
    <div className="profile-pic mr-3">
      {clientDetail.profile_image ? (
        <img src={clientDetail.profile_image} alt="" />
      ) : (
        <h3>
          {clientDetail.firstName?.toUpperCase().trim().charAt(0)}
          {clientDetail.lastName?.toUpperCase().trim().charAt(0)}
        </h3>
      )}
    </div>
  );
}

function DashboardUserHeader() {
  const [clientDetail, setClientDetail] = useState({});
  const [updatingMeetings, setUpdatingMeetings] = useState(false);
  const [subscriptionDetailsPopup, setSubscriptionDetailsPopup] = useState(false);
  const params = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const pathname = location.pathname;
  const showDocuments = pathname.includes('documents');
  const showMeetings = pathname.includes('meetings');
  // SEC-066: the Block, Subscription Details and Increment-Meeting actions
  // are gated by the server-verified role — this header only ever renders
  // inside a ProtectedRoutes subtree, so verification has already settled by
  // the time it mounts.
  const { role } = useAuth();
  const [confirmBlockPopupShow, setConfirmBlockPopupShow] = useState(false);
  const [blockAction, setBlockAction] = useState('');
  const userId = params?.clientId;
  const canIncrement = isPrivilegedRole(role?.toLowerCase());
  const getClientDetails = async () => {
    await loadClientProfile(userId, setClientDetail, enqueueSnackbar, navigate);
  };

  useEffect(() => {
    getClientDetails();
  }, [userId])

  const handleSubscriptionClick = () => {
    setSubscriptionDetailsPopup(true);
  };

  const subscriptionDetailsPopupClose = () => {
    setSubscriptionDetailsPopup(false);
  };

  const handleGoBack = () => {
    navigateBackFromClientArea(navigate, location.pathname, clientDetail?._id);
  };

  const openConfirmBlockPopup = () => {
    setBlockAction(clientDetail?.status ? 'block' : 'unblock');
    setConfirmBlockPopupShow(true);
  };

  const confirmBlockPopupClose = () => {
    setConfirmBlockPopupShow(false);
  };

  const handleBlock = async () => {
    setConfirmBlockPopupShow(false);
    try {
      const newStatus = !clientDetail?.status;
      await blockUnblockUser({
        userId: clientDetail?._id,
        status: newStatus,
      });
      const action = newStatus ? "unblocked" : "blocked";
      enqueueSnackbar(`Client ${action} successfully`, { variant: "success" });
      getClientDetails();
    } catch (error) {
      const action = clientDetail?.status ? "block" : "unblock";
      enqueueSnackbar(`Failed to ${action} client`, { variant: "error" });
    }
  };

  const handleIncrementMeetings = async () => {
    if (!clientDetail?._id) return;
    setUpdatingMeetings(true);
    
    try {
      const res = await incrementTotalMeetings(clientDetail._id);
      if (res.status == 200) {
        // Backend ARCH-036 changed what this route answers with. It used to return
        // `total_meetings`, the pooled counter the grant had just bumped; that
        // counter is gone and the grant now moves `purchased_meetings` — the bucket
        // that never expires and survives a plan change, which is what makes this
        // button a comp rather than a loan against the annual allowance. The
        // response carries that plus the recomputed `meetings_available`, so the
        // header below can show the new balance without re-reading the profile.
        setClientDetail((prev) => ({
          ...prev,
          purchased_meetings: res.purchased_meetings,
          meetings_available: res.meetings_available
        }));

        setTimeout(() => {
          setUpdatingMeetings(false);
        }, 300);

        enqueueSnackbar("Meeting credit granted", { variant: "success" });
      }
    } catch (err) {
      enqueueSnackbar("Failed to increment total meetings", { variant: "error" });
    }
  };

  const isPrivilegedAdvisor = isPrivilegedRole(role);
  const meetingsListQuery = isPrivilegedAdvisor ? '?isadmin=true' : '';
  const documentsNavActive = isDocumentsNavActive(pathname);
  const meetingsNavActive = isMeetingsNavActive(pathname);
  // #91: plan from the price nickname, status from the subscription's own
  // status, not from the billing interval and the price's `active` flag.
  const subscriptionPlanLabel = getSelectedPlanName(clientDetail?.selected_plan);
  const subscriptionStatusLabel = getSelectedPlanStatusLabel(clientDetail?.selected_plan);
  const memberSinceFormatted = formatMemberSince(clientDetail?.createdAt);
  const blockConfirmMessage = buildBlockConfirmMessage(clientDetail);

  const goToPastMeetings = () => {
    navigate(`/meetings/${clientDetail?._id}${meetingsListQuery}`);
  };

  return (
    <>
      <div className="dashboard-header mb-4 user-header">
        <div className="row">
          <div className=" col-5 d-flex dashboard-user-dtls">
            <button onClick={handleGoBack} className="common-back-btn mr-3 mt-2">
              <i className="fa fa-angle-left"></i>
            </button>

            {!showDocuments && !showMeetings &&
              <div className="d-flex">
                <ClientAvatar clientDetail={clientDetail} />
                <div>
                  <h6 className="advisor_username">
                    {clientDetail?.firstName}{" "}
                    {clientDetail?.lastName}
                  </h6>
                  <div className="d-flex gap-10">
                    <div id="email" className="userdata-item">
                      <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2.33335 13.6667C1.87502 13.6667 1.4828 13.5037 1.15669 13.1775C0.830576 12.8514 0.667243 12.4589 0.666687 12V2.00004C0.666687 1.54171 0.83002 1.14948 1.15669 0.823374C1.48335 0.497263 1.87558 0.33393 2.33335 0.333374H15.6667C16.125 0.333374 16.5175 0.496707 16.8442 0.823374C17.1709 1.15004 17.3339 1.54226 17.3334 2.00004V12C17.3334 12.4584 17.1703 12.8509 16.8442 13.1775C16.5181 13.5042 16.1256 13.6673 15.6667 13.6667H2.33335ZM9.00002 7.83337L2.33335 3.66671V12H15.6667V3.66671L9.00002 7.83337ZM9.00002 6.16671L15.6667 2.00004H2.33335L9.00002 6.16671ZM2.33335 3.66671V2.00004V12V3.66671Z" fill="#34367D" />
                      </svg>
                      {clientDetail?.email}
                    </div>
                    <div id="phone" className="userdata-item">
                      <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3.16667 1.33337H6.5L8.16667 5.50004L6.08333 6.75004C6.9758 8.55965 8.44039 10.0242 10.25 10.9167L11.5 8.83337L15.6667 10.5V13.8334C15.6667 14.2754 15.4911 14.6993 15.1785 15.0119C14.866 15.3244 14.442 15.5 14 15.5C10.7494 15.3025 7.68346 13.9221 5.38069 11.6194C3.07792 9.31658 1.69754 6.25065 1.5 3.00004C1.5 2.55801 1.67559 2.13409 1.98816 1.82153C2.30072 1.50897 2.72464 1.33337 3.16667 1.33337Z" stroke="#129389" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {clientDetail?.mobile}
                    </div>
                  </div>
                </div>

              </div>}

            {pathname.includes('documents') && <h2 className="page-heading mt-2">Documents</h2>}
            {pathname.includes('meetings') && <h2 className="page-heading mt-2">Meetings</h2>}

          </div>

          <div className=" col-7 d-flex justify-content-end gap-20">
             {/* Meetings the client can book right now. Was `consumed/total`,
                 which rendered a flat `0/0` for every client once backend ARCH-036
                 removed both counters — indistinguishable from a genuinely
                 exhausted account, on the one screen an admin uses to decide
                 whether to grant a credit. */}
            <div className="meeting-counter mt-1 justify-content-end align-items-center">
              <span className="mt-1">Meetings Available:</span>
              <span className="mt-1">
                {formatMeetingsAvailable(clientDetail)}
              </span>

              {canIncrement && (
                <button
                  className="as-btn-primary btn-sharp active ml-2"
                  onClick={handleIncrementMeetings}
                  disabled={updatingMeetings}
                >
                  {updatingMeetings ? "Updating..." : "Add Meeting"}
                </button>
              )}
            </div>

            <button type="button" className={`as-btn-primary btn-sharp mt-1 ${documentsNavActive ? 'active' : ''}`} onClick={() => navigate(`/documents/${clientDetail?._id}`)}><span>Documents</span></button>

            {/* <button type="button" className={`as-btn-primary btn-sharp mt-1 ${location.pathname.includes('meetings') || location.pathname.includes('dashboard') ? 'active' : ''}`} onClick={() => navigate(`/meetings/${clientDetail?._id}`)}><span>Past Meetings</span></button> */}
            <button
              type="button"
              className={`as-btn-primary btn-sharp mt-1 ${meetingsNavActive ? 'active' : ''}`}
              onClick={goToPastMeetings}
            >
              <span>Past Meetings</span>
            </button>

            {isPrivilegedAdvisor && (
              <button type="button" onClick={handleSubscriptionClick} className={`as-btn-primary btn-sharp mt-1 ${pathname.includes('dashboard') ? 'active' : ''}`}><span>Subscription Details</span></button>
            )}

            {isPrivilegedAdvisor && (
            <div id="block-user" className="circle-button" onClick={openConfirmBlockPopup} title={clientDetail?.status ? 'Block' : 'Unblock'} >
              <svg width="24" height="24" className={clientDetail?.status ? "" : "blocked-svg"} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_2411_158)">
                  <path d="M15.5962 13.4475C15.3937 13.575 15.1987 13.7062 15.0112 13.8412C14.8237 13.9762 14.64 14.1337 14.46 14.3137C13.7925 13.7137 13.0425 13.26 12.21 12.9525C11.3775 12.645 10.5075 12.4875 9.59996 12.48C8.59496 12.48 7.65371 12.6637 6.77621 13.0312C5.89871 13.3987 5.13746 13.9087 4.49246 14.5612C3.84746 15.2137 3.33746 15.9787 2.96246 16.8562C2.58746 17.7337 2.39996 18.675 2.39996 19.68H0.959961C0.959961 18.78 1.09121 17.91 1.35371 17.07C1.61621 16.23 1.99121 15.4575 2.47871 14.7525C2.96621 14.0475 3.55121 13.4212 4.23371 12.8737C4.91621 12.3262 5.68871 11.8987 6.55121 11.5912C6.13871 11.3287 5.76746 11.0212 5.43746 10.6687C5.10746 10.3162 4.82246 9.92621 4.58246 9.49871C4.34246 9.07121 4.15871 8.62871 4.03121 8.17121C3.90371 7.71371 3.83996 7.22996 3.83996 6.71996C3.83996 5.92496 3.98996 5.17871 4.28996 4.48121C4.58996 3.78371 5.00246 3.17621 5.52746 2.65871C6.05246 2.14121 6.66371 1.72871 7.36121 1.42121C8.05871 1.11371 8.80496 0.959961 9.59996 0.959961C10.395 0.959961 11.1412 1.10996 11.8387 1.40996C12.5362 1.70996 13.1437 2.12246 13.6612 2.64746C14.1787 3.17246 14.5912 3.78371 14.8987 4.48121C15.2062 5.17871 15.36 5.92496 15.36 6.71996C15.36 7.21496 15.2962 7.69496 15.1687 8.15996C15.0412 8.62496 14.8612 9.07121 14.6287 9.49871C14.3962 9.92621 14.1112 10.3125 13.7737 10.6575C13.4362 11.0025 13.0612 11.3137 12.6487 11.5912C13.2037 11.7862 13.725 12.0412 14.2125 12.3562C14.7 12.6712 15.1612 13.035 15.5962 13.4475ZM5.27996 6.71996C5.27996 7.31246 5.39246 7.87121 5.61746 8.39621C5.84246 8.92121 6.15371 9.37871 6.55121 9.76871C6.94871 10.1587 7.40621 10.4662 7.92371 10.6912C8.44121 10.9162 8.99996 11.0325 9.59996 11.04C10.1925 11.04 10.7512 10.9275 11.2762 10.7025C11.8012 10.4775 12.2587 10.1662 12.6487 9.76871C13.0387 9.37121 13.3462 8.91371 13.5712 8.39621C13.7962 7.87871 13.9125 7.31996 13.92 6.71996C13.92 6.12746 13.8075 5.56871 13.5825 5.04371C13.3575 4.51871 13.0462 4.06121 12.6487 3.67121C12.2512 3.28121 11.7937 2.97371 11.2762 2.74871C10.7587 2.52371 10.2 2.40746 9.59996 2.39996C9.00746 2.39996 8.44871 2.51246 7.92371 2.73746C7.39871 2.96246 6.94121 3.27371 6.55121 3.67121C6.16121 4.06871 5.85371 4.52621 5.62871 5.04371C5.40371 5.56121 5.28746 6.11996 5.27996 6.71996Z" fill="#1A1D43" />
                  <path d="M18 10.5601C18.8636 10.5601 19.6714 10.7226 20.4236 11.0476C21.1757 11.3726 21.835 11.8183 22.4014 12.3847C22.9679 12.9511 23.4136 13.6104 23.7386 14.3626C24.0636 15.1147 24.2307 15.9272 24.24 16.8001C24.24 17.6636 24.0775 18.4715 23.7525 19.2236C23.4275 19.9758 22.9818 20.6351 22.4154 21.2015C21.8489 21.7679 21.1897 22.2136 20.4375 22.5386C19.6854 22.8636 18.8729 23.0308 18 23.0401C17.1364 23.0401 16.3286 22.8776 15.5764 22.5526C14.8243 22.2276 14.165 21.7818 13.5986 21.2154C13.0322 20.649 12.5864 19.9897 12.2614 19.2376C11.9364 18.4854 11.7693 17.6729 11.76 16.8001C11.76 15.9365 11.9225 15.1286 12.2475 14.3765C12.5725 13.6243 13.0182 12.9651 13.5847 12.3986C14.1511 11.8322 14.8104 11.3865 15.5625 11.0615C16.3147 10.7365 17.1272 10.5693 18 10.5601ZM18 21.2572C18.4364 21.2572 18.8636 21.1968 19.2814 21.0761C19.6993 20.9554 20.0893 20.7697 20.4514 20.519L14.2811 14.3486C14.0397 14.7108 13.8586 15.1008 13.7379 15.5186C13.6172 15.9365 13.5522 16.3636 13.5429 16.8001C13.5429 17.4129 13.6589 17.9886 13.8911 18.5272C14.1232 19.0658 14.4436 19.5347 14.8522 19.934C15.2607 20.3333 15.7343 20.6536 16.2729 20.8951C16.8114 21.1365 17.3872 21.2572 18 21.2572ZM21.7189 19.2515C21.9604 18.8893 22.1414 18.4993 22.2622 18.0815C22.3829 17.6636 22.4479 17.2365 22.4572 16.8001C22.4572 16.1872 22.3411 15.6115 22.1089 15.0729C21.8768 14.5343 21.5564 14.0654 21.1479 13.6661C20.7393 13.2668 20.2657 12.9465 19.7272 12.7051C19.1886 12.4636 18.6129 12.3429 18 12.3429C17.5636 12.3429 17.1364 12.4033 16.7186 12.524C16.3007 12.6447 15.9107 12.8304 15.5486 13.0811L21.7189 19.2515Z" fill="#1A1D43" />
                </g>
                <defs>
                  <clipPath id="clip0_2411_158">
                    <rect width="24.96" height="24" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </div>
            )}
          </div>
        </div>

      </div>

      {/* subscription details popup */}
      <Modal
        size="md"
        className="thankYouPopup"
        show={subscriptionDetailsPopup}
        onHide={subscriptionDetailsPopupClose}
        aria-labelledby="thankYouPopupLabel"
        backdrop="static"
        keyboard={false}
        centered
      >
        <div className="modal-body">
          <div className="d-flex justify-content-end">
            <Link
              className="close-popup-btn"
              onClick={subscriptionDetailsPopupClose}
            >
              <i className="fa fa-times"></i>
            </Link>
          </div>

          <h4 className="text-center mb-4">Subscription Details</h4>
          <div className="appointment-dtls-box">
            <div className="row appointment-dtls mb-3">
              <div className="col-5 d-flex place-items-center">
                <img src={SubscriptionIcon} alt="" />
                <p className="mb-0 ml-2 f-500">Subscription Type
                </p>
              </div>
              <div className="col-7 place-content-center">
                <p>{subscriptionPlanLabel}</p>
              </div>
            </div>
            <div className="row appointment-dtls mb-3">
              <div className="col-5 d-flex place-items-center">
                <img src={detailsIcon} alt="" />
                <p className="mb-0 ml-2 f-500">Status</p>
              </div>
              <div className="col-7 place-content-center">
                <p>{subscriptionStatusLabel}</p>
              </div>
            </div>
            <div className="row appointment-dtls">
              <div className="col-5 d-flex place-items-center">
                <img src={dateIcon} alt="" />
                <p className="mb-0 ml-2 f-500">Member Since</p>
              </div>
              <div className="col-7 place-content-center">
                <p>{memberSinceFormatted}</p>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* block popup Modal*/}
      <Modal
        size="md"
        className="confirmBlockPopup customMiniPopup"
        show={confirmBlockPopupShow}
        onHide={confirmBlockPopupClose}
        aria-labelledby="confirmBlockPopupLabel"
        backdrop="static"
        keyboard={false}
        centered
      >
        <div className="modal-body">
          <div className="d-flex justify-content-between">
            <h4 className="mt-3">  </h4>
            <Link className="close-popup-btn" onClick={confirmBlockPopupClose}>
              <i className="fa fa-times"></i>
            </Link>
          </div>
          <h4 className="mt-3 mb-3 text-center">{blockConfirmMessage}</h4>
          <h5 className="text-center f-700">Are you sure?</h5>
          <div className="text-center mt-4 mb-3">
            <button className="as-btn-primary w-75" onClick={handleBlock}>
              {resolveBlockButtonLabel(blockAction)} </button>
          </div>
        </div>
      </Modal>
    </>

  );
}

export default DashboardUserHeader;
